'use client';

import React, { useEffect, useState } from 'react';
import styled, { css, keyframes } from 'styled-components';
import { Icon, type IconName } from '@liftango/ui-icons';

export type DiscMarkerFill = 'solid' | 'outline' | 'muted';

export interface DiscMarkerProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'role'> {
  /** Registry icon to render inside the disc. */
  icon: IconName;
  /** Disc background; resolved CSS color string. */
  bg: string;
  /** Icon foreground; defaults to white for `solid`/`muted`, `bg` for `outline`. */
  fg?: string;
  /** Outer ring color; defaults to white. */
  ring?: string;
  /** Visual style: `solid` (default), `outline`, `muted`. */
  fill?: DiscMarkerFill;
  /** Slow halo animation. Auto-suppressed under prefers-reduced-motion. */
  pulse?: boolean;
  /** Pulse halo color; defaults to `bg`. */
  pulseColor?: string;
  /** Top-right sequence badge. */
  sequence?: number;
  /** Bottom-right small badge icon. */
  badgeIcon?: IconName;
  /** Bottom-right badge tone color. */
  badgeColor?: string;
  /** Marker diameter in px; default 30. */
  size?: number;
  /**
   * Renders a downward stem below the disc, turning it into a lollipop pin
   * whose tip — not its centre — marks the geographic point. Use
   * {@link markerAnchorOffset} to land the tip on a map coordinate.
   */
  tail?: boolean;
  /** Rotates the inner icon by N degrees clockwise. Default 0. */
  iconRotation?: number;
  /** Promotes to role="button" when set. */
  onClick?: () => void;
  'aria-label'?: string;
}

/** Stem length in px for a tailed marker, derived from the disc diameter. */
function tailLengthFor(size: number): number {
  return Math.round(size * 0.55);
}

/**
 * The point — in px from the marker's top-left box corner — that should sit on
 * a map coordinate. For a plain disc that's the centre; for a tailed (lollipop)
 * marker it's the stem tip below the disc. Map integrations (Leaflet, Google
 * Maps `OverlayView`, etc.) feed this to their anchor/offset so the marker
 * lands precisely, independent of size or whether a tail is present.
 *
 * @example
 * const { x, y } = markerAnchorOffset({ size: 30, tail: true });
 * // position the element so (x, y) sits on the lat/lng
 */
export function markerAnchorOffset({ size, tail = false }: { size: number; tail?: boolean }): {
  x: number;
  y: number;
} {
  const x = size / 2;
  return tail ? { x, y: size + tailLengthFor(size) } : { x, y: size / 2 };
}

const pulseAnimation = keyframes`
  0%   { transform: translate(-50%, -50%) scale(0.9); opacity: 1; }
  50%  { transform: translate(-50%, -50%) scale(1.4); opacity: 0; }
  100% { transform: translate(-50%, -50%) scale(0.9); opacity: 1; }
`;

const Wrapper = styled.div<{ $size: number; $totalH: number; $clickable: boolean }>`
  box-sizing: border-box;
  position: relative;
  width: ${(p) => p.$size}px;
  height: ${(p) => p.$totalH}px;
  display: inline-block;
  flex-shrink: 0;
  cursor: ${(p) => (p.$clickable ? 'pointer' : 'default')};
`;

/**
 * Square box that hosts the disc, pulse, and corner badges. Kept at the disc's
 * own size and pinned to the top of the wrapper so badge offsets stay anchored
 * to the disc even when a tail extends the wrapper downward.
 */
const DiscBox = styled.div<{ $size: number }>`
  position: relative;
  width: ${(p) => p.$size}px;
  height: ${(p) => p.$size}px;
  z-index: 1;
`;

/** Lollipop stem — sits behind the disc and runs down to the anchor tip. */
const Stem = styled.div<{ $size: number; $len: number; $color: string }>`
  position: absolute;
  top: ${(p) => p.$size - Math.round(p.$size * 0.12)}px;
  left: 50%;
  transform: translateX(-50%);
  width: ${(p) => Math.max(3, Math.round(p.$size * 0.16))}px;
  height: ${(p) => p.$len + Math.round(p.$size * 0.12)}px;
  background: ${(p) => p.$color};
  border-bottom-left-radius: ${(p) => Math.max(2, Math.round(p.$size * 0.08))}px;
  border-bottom-right-radius: ${(p) => Math.max(2, Math.round(p.$size * 0.08))}px;
  z-index: 0;
  pointer-events: none;
`;

const IconRotate = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

const Disc = styled.div<{
  $size: number;
  $bg: string;
  $fg: string;
  $ring: string;
  $fill: DiscMarkerFill;
}>`
  box-sizing: border-box;
  width: ${(p) => p.$size}px;
  height: ${(p) => p.$size}px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 3px solid ${(p) => p.$ring};
  box-shadow: ${(p) => p.theme.elevation.raised.shadow};
  transition: transform 0.15s ease;
  ${(p) =>
    p.$fill === 'solid' &&
    css`
      background: ${p.$bg};
      color: ${p.$fg};
    `}
  ${(p) =>
    p.$fill === 'outline' &&
    css`
      background: transparent;
      color: ${p.$fg};
      border-color: ${p.$bg};
    `}
  ${(p) =>
    p.$fill === 'muted' &&
    css`
      background: ${p.$bg};
      color: ${p.$fg};
      opacity: 0.6;
    `}

  ${Wrapper}:hover & {
    transform: scale(1.08);
  }
`;

const Pulse = styled.div<{ $size: number; $color: string }>`
  position: absolute;
  top: 50%;
  left: 50%;
  width: ${(p) => p.$size + 6}px;
  height: ${(p) => p.$size + 6}px;
  border-radius: 50%;
  background: radial-gradient(
    circle,
    transparent 55%,
    ${(p) => p.$color} 55%,
    ${(p) => p.$color} 100%
  );
  opacity: 0.5;
  animation: ${pulseAnimation} 3.5s cubic-bezier(0.455, 0.03, 0.515, 0.955) infinite;
  pointer-events: none;
`;

const Sequence = styled.div`
  position: absolute;
  top: -6px;
  right: -6px;
  background: ${(p) => p.theme.colors.neutral.panel};
  color: ${(p) => p.theme.colors.neutral.ink};
  border: 2px solid ${(p) => p.theme.colors.neutral.lineStrong};
  border-radius: 50%;
  width: 18px;
  height: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: ${(p) => p.theme.fontFamily.mono};
  font-size: 10px;
  font-weight: ${(p) => p.theme.fontWeight.bold};
  pointer-events: none;
  box-shadow: ${(p) => p.theme.elevation.raised.shadow};
`;

const Badge = styled.div<{ $color?: string }>`
  position: absolute;
  bottom: -4px;
  right: -4px;
  background: ${(p) => p.theme.colors.neutral.panel};
  color: ${(p) => p.$color ?? p.theme.colors.neutral.ink};
  border: 2px solid ${(p) => p.$color ?? p.theme.colors.neutral.ink};
  border-radius: 50%;
  width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
  box-shadow: ${(p) => p.theme.elevation.raised.shadow};
`;

function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReduced(mq.matches);
    const handler = (e: MediaQueryListEvent) => setReduced(e.matches);
    mq.addEventListener?.('change', handler);
    return () => mq.removeEventListener?.('change', handler);
  }, []);
  return reduced;
}

/**
 * Generic circular map marker. Domain-agnostic — accepts resolved CSS colors,
 * not theme tones. Domain wrappers (`EventMarker`, `StopPin`) map their vocabularies
 * to these props.
 *
 * Visual slots: disc (icon), optional pulse halo, optional top-right sequence
 * badge, optional bottom-right status badge, configurable outer ring.
 */
export function DiscMarker({
  icon,
  bg,
  fg,
  ring = '#ffffff',
  fill = 'solid',
  pulse = false,
  pulseColor,
  sequence,
  badgeIcon,
  badgeColor,
  size = 30,
  tail = false,
  iconRotation = 0,
  onClick,
  'aria-label': ariaLabel,
  ...rest
}: DiscMarkerProps) {
  const reduced = usePrefersReducedMotion();
  const interactive = typeof onClick === 'function';
  const iconSize = Math.round(size * 0.55);
  const resolvedFg = fg ?? (fill === 'outline' ? bg : '#ffffff');
  const showPulse = pulse && !reduced;
  const tailLength = tailLengthFor(size);
  const totalH = tail ? size + tailLength : size;
  // The stem reads as a continuation of the route line, so it carries the
  // marker tone in every fill mode.
  const stemColor = bg;

  return (
    <Wrapper
      $size={size}
      $totalH={totalH}
      $clickable={interactive}
      role={interactive ? 'button' : 'img'}
      tabIndex={interactive ? 0 : undefined}
      aria-label={ariaLabel}
      onClick={onClick}
      onKeyDown={
        interactive
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClick?.();
              }
            }
          : undefined
      }
      {...rest}
    >
      {tail && <Stem data-disc-tail $size={size} $len={tailLength} $color={stemColor} />}
      <DiscBox $size={size}>
        {showPulse && <Pulse data-disc-pulse $size={size} $color={pulseColor ?? bg} />}
        <Disc
          data-disc
          data-fill={fill}
          style={{ background: fill === 'outline' ? 'transparent' : bg, borderColor: ring }}
          $size={size}
          $bg={bg}
          $fg={resolvedFg}
          $ring={ring}
          $fill={fill}
        >
          <IconRotate
            data-disc-icon
            style={{ transform: iconRotation ? `rotate(${iconRotation}deg)` : undefined }}
          >
            <Icon name={icon} size={iconSize} aria-hidden />
          </IconRotate>
        </Disc>
        {typeof sequence === 'number' && <Sequence data-disc-sequence>{sequence}</Sequence>}
        {badgeIcon && (
          <Badge data-disc-badge $color={badgeColor}>
            <Icon name={badgeIcon} size={9} aria-hidden />
          </Badge>
        )}
      </DiscBox>
    </Wrapper>
  );
}
